import { useEffect, useState } from 'preact/hooks';
import { equal, jsonDateTimeWithoutTimezone } from '../utils';
import { logout } from './login';
import { getAccessToken, tryRefreshToken } from './tokens';
import { sha256 } from "crypto-hash";

export const baseUrl = document.location.hostname === "localhost" ? "https://localhost:7285" : "";

let refreshCount = 0;

interface GraphQlResult<T> {
    data: T
    errors?: {
        message: string,
        extensions?: any
    }[];
};

export async function fetchGraphql<T, TVars>(name: string, query: string, variables: TVars, authenticate = true, refresh = true, useApq = true): Promise<GraphQlResult<T>> {
    const sha256Hash = await sha256(query);

    const operation = {
        "operationName": name,
        "variables": variables,
        "query": useApq ? undefined : query,
        "extensions": !useApq ? undefined : {
            "persistedQuery": {
                "sha256Hash": sha256Hash,
                "version": 1
            }
        }
    };

    const headers = {
        "content-type": "application/json;charset=utf-8",
        ... (!authenticate ? {} : {
            "Authorization": "Bearer " + getAccessToken()
        })
    };

    let res = await fetch(baseUrl + "/graphql", {
        method: "POST",
        headers,
        body: JSON.stringify(operation, jsonReplacer)
    });

    let json = (await res.json()) as GraphQlResult<T>;

    // Handle AutomaticPersistedQueries
    if (useApq && isApqError(json)) {
        operation.query = query;
        res = await fetch(baseUrl + "/graphql", {
            method: "POST",
            headers,
            body: JSON.stringify(operation)
        });
        json = (await res.json()) as GraphQlResult<T>;
    }

    // handle refreshing the token and retrying the query if we get an unauthorized response
    if (authenticate) {
        if (isUnauthorizedError(json)) {
            refreshCount++;
            if (refresh && refreshCount < 10) {
                if (await tryRefreshToken()) {
                    return await fetchGraphql<T, TVars>(name, query, variables, true, false);
                }
            }
            refreshCount = 0;
            logout();
        }
        else if (refreshCount > 0) refreshCount = Math.max(0, refreshCount - 1);
    }

    return json;
}

export function useGraphql<T, TVars>(name: string, query: string, variables: TVars) {
    const [result, setResult] = useState<T | null>(null);
    const [previousVars, setPreviousVars] = useState<TVars>(variables);

    if (!equal(previousVars, variables)) {
        setPreviousVars(variables);
    }

    useEffect(() => {
        fetchGraphql<T, TVars>(name, query, variables)
            .then(response => {
                setResult(response.data);
            });
    }, [name, query, previousVars]);

    return { result };
}

export function isUnauthorizedError<T>(result: GraphQlResult<T>) {
    return (result.errors && result.errors.some(n => n.extensions?.code === "UNAUTHORIZED_ACCESS" || -1 !== n.message.toLowerCase().indexOf("unauthorized"))) || false;
}

export function isApqError<T>(result: GraphQlResult<T>) {
    return (result.errors && result.errors.some(n => n.extensions?.code === "PERSISTED_QUERY_NOT_FOUND" || -1 !== n.message.toLowerCase().indexOf("PersistedQueryNotFound"))) || false;
}

function jsonReplacer(key: string, value: string) {
    // @ts-ignore
    if ((this as unknown as any)[key] instanceof Date) {
        // @ts-ignore
        return jsonDateTimeWithoutTimezone((this as any)[key]);
    }
    return value;
}
